/**
 * Field
 */

import React, { useState, useRef, useEffect } from 'react';
import { useFieldValidation } from '../hooks/useFieldValidation';
import { FieldProps } from 'types/form';

import { useFormValue } from '../context/Form.context';
import { Input, ClearButton } from '../Form.styles';
import Icon from 'components/Icon';
import { ScreenReaderText } from 'components/Text';

const Field: React.FC<FieldProps> = (props) => {
	const {
		className,
		placeholder,
		description,
		onFieldChange,
		defaultValue,
		autoSubmit = false,
		autoSubmitOnClear = false,
		name,
		id,
		showError,
		setFocus,
		visibleFields,
		noValidate,
		type,
		label,
		describedby,
		required,
		resetForm,
		patternMessage,
		validationMessage,
		rounded,
		clearable,
		...allProps
	} = props;

	const [{ invalidFields }, dispatch] = useFormValue();
	const [value, setValue] = useState(defaultValue || '');
	const [valid, setValid] = useState(false);
	const [touched, setTouched] = useState(false);
	const fieldRef = useRef<any>(null);
	const [validateField, showFieldError, hideFieldError] = useFieldValidation(
		fieldRef,
		props
	);

	useEffect(() => {
		const validatedField = validateField(value);
		const fieldIsValid = !validatedField.message;

		if (validatedField.message) {
			setValid(false);
			dispatch({ type: 'FIELD_INVALID', field: validatedField });
		} else {
			setValid(true);

			if (invalidFields.includes(validatedField.id)) {
				dispatch({ type: 'FIELD_VALID', field: validatedField });
			}
		}

		if (!noValidate) {
			if (touched && !fieldIsValid) {
				showFieldError(validatedField);
			} else if (
				(showError &&
					!fieldIsValid &&
					visibleFields &&
					visibleFields.includes(id)) ||
				(showError && !fieldIsValid && !visibleFields)
			) {
				setTouched(true);
				showFieldError(validatedField);
			} else {
				hideFieldError();
			}

			if (setFocus) {
				fieldRef.current.focus();
			}
		}
	}, [touched, showError, value, setFocus, visibleFields, required]);

	const onChange = (e: React.FormEvent<HTMLElement>) => {
		const target = e.target as HTMLFormElement;

		setValue(target.value);
		onFieldChange({
			name,
			value: target.value,
			type: 'Field',
			autoSubmit,
		});
	};

	const describedByAttribute = `form${id}__desc${
		describedby ? ` ${describedby}` : ''
	}`;

	return (
		<>
			<Input
				className={className || ''}
				title={description}
				placeholder={placeholder || ''}
				aria-invalid={!valid}
				value={value}
				name={name}
				required={required}
				onChange={onChange}
				onBlur={() => setTouched(true)}
				ref={fieldRef}
				id={id}
				aria-describedby={!noValidate ? describedByAttribute : undefined}
				type={type === null ? 'text' : type}
				rounded={rounded}
				{...allProps}
			/>
			{clearable && value !== '' && (
				<ClearButton
					onClick={() => {
						setValue('');
						onFieldChange({
							name,
							value: '',
							type: 'Field',
							autoSubmit: autoSubmitOnClear,
						});
					}}
					type="button"
				>
					<ScreenReaderText>Rensa {label}</ScreenReaderText>
					<Icon icon="close" aria-hidden="true" />
				</ClearButton>
			)}
		</>
	);
};

export default Field;
